import { useState } from 'react';
import { X, Download, FileJson, FileCode, Database, BookText, Image, Workflow } from 'lucide-react';

const ExportFormatModal = ({ isOpen, onClose, onExport, modelName }) => {
  const [selectedFormat, setSelectedFormat] = useState('xmi');

  if (!isOpen) return null;

  const handleExport = () => {
    onExport(selectedFormat);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center gap-2">
            <Download className="text-blue-600 dark:text-blue-400" size={20} />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              Export Model
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Close"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Choose the export format for <strong>{modelName}</strong>:
          </p>

          {/* Grid Layout - 2 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">

            {/* XMI Option */}
            <button
              onClick={() => setSelectedFormat('xmi')}
              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                selectedFormat === 'xmi'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 ${selectedFormat === 'xmi' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
                  <FileCode size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                      XMI Format
                    </h3>
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                      Recommended
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    XML Metadata Interchange (UML 2.5)
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    ✓ Enterprise Architect, StarUML
                  </p>
                </div>
              </div>
            </button>

            {/* SQL Option */}
            <button
              onClick={() => setSelectedFormat('sql')}
              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                selectedFormat === 'sql'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 ${selectedFormat === 'sql' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}`}>
                  <Database size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                      SQL DDL Format
                    </h3>
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                      Implementation
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    ANSI SQL for database creation
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    ✓ Tables, Views, Indexes, FKs
                  </p>
                </div>
              </div>
            </button>

            {/* Mermaid ERD Option */}
            <button
              onClick={() => setSelectedFormat('mermaid')}
              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                selectedFormat === 'mermaid'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 ${selectedFormat === 'mermaid' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`}>
                  <Workflow size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                      Mermaid ERD
                    </h3>
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 rounded">
                      Diagram
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Entity Relationship Diagram (.mmd)
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    ✓ GitHub, Notion, Mermaid Live
                  </p>
                </div>
              </div>
            </button>

            {/* JSON Option */}
            <button
              onClick={() => setSelectedFormat('json')}
              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                selectedFormat === 'json'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 ${selectedFormat === 'json' ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                  <FileJson size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                      JSON Format
                    </h3>
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                      Legacy
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    SchemaGraphos native format
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    ✓ Lightweight, Git-friendly
                  </p>
                </div>
              </div>
            </button>

            {/* Data Dictionary Option */}
            <button
              onClick={() => setSelectedFormat('dictionary')}
              className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                selectedFormat === 'dictionary'
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 ${selectedFormat === 'dictionary' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}>
                  <BookText size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                      Data Dictionary
                    </h3>
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 rounded">
                      Documentation
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Complete Markdown documentation with entities, fields and business rules
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    ✓ Markdown format (.md) with full model documentation
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* ── Image Export Section ───────────────────────────────────── */}
          <div className="mt-4 mb-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              <Image size={13} />
              Diagram Image Export
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

              {/* PNG */}
              <button
                onClick={() => setSelectedFormat('png')}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  selectedFormat === 'png'
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`mt-0.5 ${selectedFormat === 'png' ? 'text-sky-600 dark:text-sky-400' : 'text-gray-400'}`}>
                    <Image size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">PNG</h3>
                      <span className="px-1.5 py-0.5 text-xs font-medium bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300 rounded">
                        Recommended
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      ✓ Lossless, transparent background
                    </p>
                  </div>
                </div>
              </button>

              {/* JPEG */}
              <button
                onClick={() => setSelectedFormat('jpeg')}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  selectedFormat === 'jpeg'
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`mt-0.5 ${selectedFormat === 'jpeg' ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400'}`}>
                    <Image size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">JPEG</h3>
                      <span className="px-1.5 py-0.5 text-xs font-medium bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300 rounded">
                        Compact
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      ✓ Smaller file, white background
                    </p>
                  </div>
                </div>
              </button>

              {/* SVG Image */}
              <button
                onClick={() => setSelectedFormat('svg-image')}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  selectedFormat === 'svg-image'
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`mt-0.5 ${selectedFormat === 'svg-image' ? 'text-violet-600 dark:text-violet-400' : 'text-gray-400'}`}>
                    <Image size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">SVG</h3>
                      <span className="px-1.5 py-0.5 text-xs font-medium bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 rounded">
                        Vector
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      ✓ Infinitely scalable, editable
                    </p>
                  </div>
                </div>
              </button>

            </div>
          </div>

          {/* Note */}
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <strong>Note:</strong> XMI and JSON formats can be imported back into SchemaGraphos. SQL, Mermaid ERD and Data Dictionary are for documentation/diagramming. PNG, JPEG and SVG capture the diagram exactly as it appears on the canvas.
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 sticky bottom-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors ${
              selectedFormat === 'xmi'
                ? 'bg-blue-600 hover:bg-blue-700'
                : selectedFormat === 'sql'
                ? 'bg-purple-600 hover:bg-purple-700'
                : selectedFormat === 'mermaid'
                ? 'bg-indigo-600 hover:bg-indigo-700'
                : selectedFormat === 'dictionary'
                ? 'bg-amber-600 hover:bg-amber-700'
                : selectedFormat === 'png'
                ? 'bg-sky-600 hover:bg-sky-700'
                : selectedFormat === 'jpeg'
                ? 'bg-teal-600 hover:bg-teal-700'
                : selectedFormat === 'svg-image'
                ? 'bg-violet-600 hover:bg-violet-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            <Download size={16} />
            {selectedFormat === 'dictionary'
              ? 'Export Data Dictionary'
              : selectedFormat === 'svg-image'
              ? 'Export as SVG Image'
              : selectedFormat === 'mermaid'
              ? 'Export as Mermaid ERD'
              : `Export as ${selectedFormat.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportFormatModal;
